import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import {
  ArrowUpRight, IdentificationCard, Certificate, ArrowsClockwise,
  Copy, GlobeHemisphereEast, PencilSimpleLine, CheckCircle,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { MaskedLines, Reveal } from "@/components/Reveal";
import Footer from "@/components/Footer";

const HERO_IMG = "https://images.pexels.com/photos/18048834/pexels-photo-18048834.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DRIVE_IMG = "https://images.unsplash.com/photo-1515086828834-023d61380316?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxzdGVlcmluZyUyMHdoZWVsJTIwZGV0YWlsJTIwZHJpdmluZ3xlbnwwfHx8fDE3ODQ1MTEyODl8MA&ixlib=rb-4.1.0&q=85";
const PORTRAIT = "https://images.unsplash.com/photo-1777637802987-fb30ac38e58a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxjb25maWRlbnQlMjBpbmRpYW4lMjBkcml2ZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODQ1MTEyODl8MA&ixlib=rb-4.1.0&q=85";

const ICONS = {
  "learners-license": IdentificationCard,
  "permanent-license": Certificate,
  "license-renewal": ArrowsClockwise,
  "duplicate-license": Copy,
  "international-permit": GlobeHemisphereEast,
  "address-change": PencilSimpleLine,
};

const CHAPTERS = [
  { n: "01", t: "Tell us what you need", d: "Pick your service and share your details in a simple guided form. No jargon, no confusion." },
  { n: "02", t: "We prepare everything", d: "Our team verifies your documents and files the paperwork with the RTO on your behalf." },
  { n: "03", t: "Track in real time", d: "Watch your application move through each stage from your personal dashboard." },
  { n: "04", t: "Collect your license", d: "Get notified the moment it's ready. Zero queues, zero running around." },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16">
      <motion.div style={{ scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Night highway" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background" />
      </motion.div>

      <motion.div style={{ y: y2 }} className="absolute right-6 top-1/4 hidden lg:block z-10">
        <div className="border border-primary/40 bg-black/50 backdrop-blur-xl p-5 w-64 rounded-sm rotate-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-2">Driving License</p>
          <p className="font-display font-black text-xl tracking-tighter">JH-01 20260720</p>
          <div className="mt-3 h-16 bg-gradient-to-r from-primary/20 to-transparent rounded-sm" />
          <p className="font-mono text-[10px] text-muted-foreground mt-2">RANCHI · JHARKHAND</p>
        </div>
      </motion.div>

      <motion.div style={{ y: y1 }} className="relative z-20 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-primary" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Ranchi · Jharkhand</span>
        </div>
        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.88] text-5xl sm:text-7xl lg:text-8xl">
          <MaskedLines lines={["Your driving", "license,"]} delay={0.1} />
          <MaskedLines lines={["without the"]} delay={0.35} lineClass="text-primary" />
          <span className="block overflow-hidden">
            <motion.span
              className="block text-stroke"
              initial={{ y: "110%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            >
              headache.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-12 max-w-3xl"
        >
          <p className="text-muted-foreground text-base leading-relaxed max-w-md">
            We handle the forms, the RTO visits and the follow-ups. You just apply once and track everything online.
          </p>
          <Link
            to="/apply"
            data-testid="hero-apply-btn"
            className="group inline-flex items-center gap-2 px-7 py-4 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest font-medium rounded-sm hover:gap-4 transition-all whitespace-nowrap"
          >
            Start Application
            <ArrowUpRight size={18} weight="bold" className="group-hover:rotate-45 transition-transform" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const spans = ["md:col-span-2 md:row-span-2", "", "", "md:col-span-2", "", ""];

  return (
    <section id="services" className="relative py-24 sm:py-32 max-w-7xl mx-auto px-5 sm:px-8 scroll-mt-16">
      <Reveal className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">What we do</span>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl mt-3 leading-none">
            Every license,<br />one place.
          </h2>
        </div>
        <p className="text-muted-foreground max-w-xs text-sm">Six core services covering the entire lifecycle of your driving license.</p>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[minmax(200px,auto)] gap-4">
        {services.map((s, i) => {
          const Icon = ICONS[s.slug] || IdentificationCard;
          return (
            <Reveal key={s.slug} delay={i * 0.06} className={spans[i % spans.length]}>
              <Link
                to="/apply"
                data-testid={`service-card-${s.slug}`}
                className="group h-full flex flex-col justify-between p-7 bg-card border border-border rounded-sm hover:border-primary transition-colors relative overflow-hidden"
              >
                <div className="flex items-start justify-between">
                  <Icon size={40} weight="light" className="text-primary" />
                  <ArrowUpRight size={22} className="text-muted-foreground group-hover:text-primary group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                </div>
                <div className="mt-8">
                  <h3 className="font-display font-bold uppercase tracking-tight text-xl sm:text-2xl leading-tight">{s.title}</h3>
                  <p className="text-muted-foreground text-sm mt-2">{s.tagline}</p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-primary mt-4">⏱ {s.duration}</p>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function Process() {
  return (
    <section id="process" className="relative py-24 sm:py-32 border-y border-border scroll-mt-16">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="mb-16">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">The Manifesto</span>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl mt-3">How it works</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-14">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="flex gap-6 border-t border-border pt-6">
                <span className="font-mono text-primary text-lg font-bold shrink-0">{c.n}</span>
                <div>
                  <h3 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl">{c.t}</h3>
                  <p className="text-muted-foreground mt-3 leading-relaxed max-w-md">{c.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Ribbon() {
  return (
    <section className="py-16 overflow-hidden bg-primary">
      <Marquee speed={40} gradient={false} autoFill>
        <span className="font-display font-black uppercase tracking-tighter text-primary-foreground text-6xl sm:text-8xl mx-8">
          {"Learner's · Permanent · Renewal · Duplicate · International · Address Change · "}
        </span>
      </Marquee>
    </section>
  );
}

function Testimonial() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  return (
    <section ref={ref} className="py-24 sm:py-32 max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <motion.div style={{ y }} className="relative">
          <div className="aspect-[4/5] overflow-hidden rounded-sm border border-border max-w-sm">
            <img src={PORTRAIT} alt="Happy driver" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
          </div>
          <div className="absolute -bottom-5 -right-3 bg-primary text-primary-foreground p-5 rounded-sm max-w-[180px]">
            <p className="font-display font-black text-4xl leading-none">4,200+</p>
            <p className="font-mono text-[10px] uppercase tracking-widest mt-1">Licenses processed</p>
          </div>
        </motion.div>
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Trusted locally</span>
          <blockquote className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mt-6">
            {"\u201CI applied on a Monday and had my learner\u2019s license without visiting the RTO even once. Ranchi Digital Seva did all the running.\u201D"}
          </blockquote>
          <p className="font-mono text-sm text-muted-foreground mt-6">— Ravi Kumar, Harmu, Ranchi</p>
        </Reveal>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-secondary border border-border rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors placeholder:text-muted-foreground";

  return (
    <section id="contact" className="py-24 sm:py-32 border-t border-border scroll-mt-16">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-16">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Get in touch</span>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl mt-3 leading-none">
            Have a<br />question?
          </h2>
          <p className="text-muted-foreground mt-6 max-w-sm leading-relaxed">
            Send us a message and our team will get back to you the same working day. Or just start your application directly.
          </p>
          <div className="mt-8 space-y-3">
            {["Free document consultation", "End-to-end RTO handling", "Real-time status tracking"].map((f) => (
              <div key={f} className="flex items-center gap-3 text-sm">
                <CheckCircle size={18} weight="fill" className="text-primary" /> {f}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-4 bg-card border border-border p-7 rounded-sm">
            <input required data-testid="contact-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input required type="email" data-testid="contact-email" className={field} placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="contact-phone" className={field} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <textarea required rows={4} data-testid="contact-message" className={field} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button
              type="submit" disabled={loading} data-testid="contact-submit"
              className="w-full py-4 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest font-medium rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <main>
      <Hero />
      <Services />
      <Process />
      <Ribbon />
      <Testimonial />
      <Contact />
      <Footer />
    </main>
  );
}
